import React, { useState, useEffect } from "react";
import { authAPI } from "../../api/authApi";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, Phone, Fingerprint, Lock, Save, Loader2, Settings } from "lucide-react";
import styles from './user.module.css';

function UserPage() {
    const [formData, setFormData] = useState({
        name: "", email: "", phone: "", cpf: "", password: ""
    });
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Funções de Máscara
    const maskCPF = (value) => {
        return value
            .replace(/\D/g, "")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1");
    };

    const maskPhone = (value) => {
        return value
            .replace(/\D/g, "")
            .replace(/(\d{2})(\d)/, "($1) $2")
            .replace(/(\d{5})(\d)/, "$1-$2")
            .replace(/(-\d{4})\d+?$/, "$1");
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await authAPI.get_client_info();
                if (response?.data) {
                    const { name, email, phone, cpf } = response.data;
                    setFormData(prev => ({
                        ...prev,
                        name: name || "",
                        email: email || "",
                        phone: phone ? maskPhone(phone) : "",
                        cpf: cpf ? maskCPF(cpf) : ""
                    }));
                }
            } catch (error) {
                setMessage({ type: "error", text: "Não foi possível carregar seus dados." });
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === "cpf") formattedValue = maskCPF(value);
        if (name === "phone") formattedValue = maskPhone(value);

        setFormData(prev => ({ ...prev, [name]: formattedValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Remove as máscaras antes de enviar para o banco
        const dataToSubmit = {
            ...formData,
            phone: formData.phone.replace(/\D/g, ""),
            cpf: formData.cpf.replace(/\D/g, "")
        };

        try {
            const response = await authAPI.update_client_info(dataToSubmit);
            if (response.status === 200) {
                setMessage({ type: "success", text: "Perfil atualizado com sucesso!" });
                setFormData(prev => ({ ...prev, password: "" }));
            }
        } catch (error) {
            setMessage({
                type: "error",
                text: error.response?.data?.msg || "Erro ao salvar as alterações."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className={styles.loaderContainer}>
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                <Loader2 size={40} color="#556b2f" />
            </motion.div>
        </div>
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.blobBlue} />
            <div className={styles.blobGold} />

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={styles.card}
            >
                <header className={styles.header}>
                    <div className={styles.iconHeader}>
                        <Settings size={22} className={styles.olive} />
                        <h1 className={styles.title}>Minha Conta</h1>
                    </div>
                    <p className={styles.subtitle}>Gerencie suas informações e segurança.</p>
                </header>

                <AnimatePresence mode="wait">
                    {message.text && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0 }}
                            className={`${styles.alert} ${styles[message.type]}`}
                        >
                            {message.text}
                        </motion.div>
                    )}
                </AnimatePresence>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.inputGroup}>
                        <label><User size={14} /> Nome Completo</label>
                        <input name="name" value={formData.name} onChange={handleChange} />
                    </div>

                    <div className={styles.inputGroup}>
                        <label><Mail size={14} /> E-mail</label>
                        <input name="email" type="email" value={formData.email} onChange={handleChange} />
                    </div>

                    <div className={styles.inputGroup}>
                        <label><Phone size={14} /> Telefone</label>
                        <input name="phone" value={formData.phone} onChange={handleChange} maxLength={15} placeholder="(00) 00000-0000" />
                    </div>

                    <div className={styles.inputGroup}>
                        <label><Fingerprint size={14} /> CPF</label>
                        <input
                            name="cpf"
                            value={formData.cpf}
                            onChange={handleChange} // Agora ele escuta as mudanças
                            maxLength={14}          // Limite de caracteres para o formato 000.000.000-00
                            placeholder="000.000.000-00"
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label>
                            <Lock size={14} /> Alterar Senha
                            <span className={styles.optional}>(deixe vazio para manter)</span>
                        </label>
                        <input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Nova senha segura" />
                    </div>

                    <button type="submit" disabled={isSubmitting} className={styles.saveButton}>
                        {isSubmitting ? <Loader2 className={styles.spin} size={20} /> : <><Save size={18} /> Salvar Alterações</>}
                    </button>
                </form>
            </motion.div>
        </div>
    );
}

export default UserPage;